import type { TGetCommunicationTemplates } from "@api";
import { CommunicationTemplateType } from "@bbj-types/communication/enum";
import type { IVariable } from "@bbj-types/utils";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Authorizer } from "@navigations/Authorizer";
import type { CreateAction } from "@root/utils/enums/communication";
import { ErrorBoundary } from "@styleguide";
import { Dialog } from "@surfaces/Dialog";
import { Suspense, useState } from "react";
import type { Control, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { CandidateCoachingStep, type ICoachingStepForm } from "./candidateCoachingStep";
import { CreateActions } from "./createAction";
import { NotifyCandidateModalDescription } from "./description";
import { EmailModalContent } from "./emailModalContent";
import { useNotifyCandidateModal } from "./logic";
import { SmsModalForm } from "./smsModalContent";
import { NotifyCandidateModalTitle } from "./title";
import type { INotifyByEmailModalForm, INotifyBySmsModalForm, TCandidateData } from "./types";

export interface IEmailModalProps<T extends TGetCommunicationTemplates> {
  open: boolean;
  handleClose: () => void;
  candidates: readonly TCandidateData[];
  type: CommunicationTemplateType;
  getTemplates: () => T;
  onSubmitEmail: (data: INotifyByEmailModalForm) => Promise<void>;
  onSubmitSms: (data: INotifyBySmsModalForm) => Promise<void>;
  onSuccessEmail?: () => Promise<void> | void;
  onSuccessSMS?: () => Promise<void> | void;
  submitLoading: boolean;
  variables: IVariable[];
  onParticipantsModified?: (modifiedParticipants: Set<string>) => void;
  createAction?: CreateAction;
}

export const NotifyCandidateModal = <T extends TGetCommunicationTemplates>({
  open,
  candidates,
  type,
  getTemplates,
  onSubmitEmail,
  onSubmitSms,
  onSuccessEmail,
  onSuccessSMS,
  submitLoading,
  handleClose,
  variables,
  onParticipantsModified,
}: IEmailModalProps<T>) => {
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const { eligibleCandidates, handleSubmit, control, setValue, getValues, loading } = useNotifyCandidateModal(
    candidates,
    onSubmitEmail,
    onSubmitSms,
    submitLoading,
    handleClose,
    open,
    type,
    onSuccessEmail,
    onSuccessSMS,
    onParticipantsModified
  );

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      disableEnforceFocus
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit,
          sx: {
            minHeight: "400px",
          },
        },
      }}
    >
      <DialogTitle component={Stack} direction="row" justifyContent="space-between" alignItems="center">
        <NotifyCandidateModalTitle type={type} />
        <IconButton onClick={handleClose} sx={{ p: 0 }}>
          <CloseIcon color="primary" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <ErrorBoundary fallback={null}>
          <Suspense fallback={<CircularProgress sx={{ margin: "auto" }} />}>
            <NotifyCandidateModalDescription
              type={type}
              candidatesCount={candidates.length}
              eligibleCandidatesCount={eligibleCandidates.length}
            />
            <Stack
              sx={{
                gap: 3,
              }}
            >
              <Typography variant="subtitle1SemiBold" color="primary">
                Contenu
              </Typography>
              <Stack
                sx={{
                  gap: 2,
                }}
              >
                {type === CommunicationTemplateType.EMAIL ? (
                  <EmailModalContent
                    candidates={candidates}
                    control={control as Control<INotifyByEmailModalForm, void>}
                    setValue={setValue as UseFormSetValue<INotifyByEmailModalForm>}
                    getTemplates={getTemplates}
                    variables={variables}
                    setAttachmentsLoading={setAttachmentsLoading}
                  />
                ) : (
                  <SmsModalForm
                    candidates={candidates}
                    control={control as Control<INotifyBySmsModalForm, void>}
                    setValue={setValue as UseFormSetValue<INotifyBySmsModalForm>}
                    getValues={getValues as UseFormGetValues<INotifyBySmsModalForm>}
                    getTemplates={getTemplates}
                    variables={variables}
                  />
                )}
              </Stack>
            </Stack>
            <Stack
              sx={{
                pt: 1,
                gap: 2,
              }}
            >
              <Typography variant="subtitle1SemiBold" color="primary">
                Options suite à cet envoi
              </Typography>
              <Typography variant="body1">
                Suite à cet envoi vous avez le choix de faire un certain nombre de changements sur la fiche de chaque
                destinataire :
              </Typography>
              <Stack
                direction="row"
                sx={{
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <CandidateCoachingStep
                  control={control as unknown as Control<ICoachingStepForm, void>}
                  setValue={setValue as unknown as UseFormSetValue<ICoachingStepForm>}
                />
              </Stack>
              <Authorizer
                requiredPermissions={["communication.action.create"]}
                Authorized={CreateActions}
                AuthorizedProps={{ control: control as unknown as Control<{ createAction: CreateAction }>, type }}
              />
            </Stack>
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" disabled={false} onClick={handleClose}>
          Annuler
        </Button>
        <Tooltip
          title="Des documents sont en cours de téléchargement. Veuillez patienter quelques secondes avant de pouvoir envoyer ce courrier."
          disableHoverListener={!attachmentsLoading}
          arrow
        >
          <span>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || attachmentsLoading || eligibleCandidates.length === 0}
              loading={loading}
            >
              Envoyer
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};
